import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useSnackbar } from '../context/SnackbarContext';
import { rememberAuthRedirect } from '../utils/authRedirect';

const Signup: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [verificationEmail, setVerificationEmail] = useState<string | null>(null);
  const { signup } = useAuth();
  const { showSnackbar } = useSnackbar();
  const [searchParams] = useSearchParams();
  const redirect = searchParams.get('redirect');

  useEffect(() => {
    rememberAuthRedirect(redirect);
  }, [redirect]);

  const loginPath = redirect ? `/login?redirect=${encodeURIComponent(redirect)}` : '/login';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (password !== confirmPassword) {
      showSnackbar('Passwords do not match.', { variant: 'error' });
      return;
    }

    if (password.length < 8) {
      showSnackbar('Password must be at least 8 characters long.', { variant: 'error' });
      return;
    }

    setLoading(true);

    try {
      await signup(email, password, name || undefined);
      setVerificationEmail(email);
    } catch (err: any) {
      showSnackbar(err.message || 'Signup failed. Please try again.', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  if (verificationEmail) {
    return (
      <div className="auth-container">
        <div className="auth-box">
          <h1>Check Your Email</h1>
          <p className="auth-subtitle">
            We sent a verification link to <strong>{verificationEmail}</strong>.
          </p>
          <div className="info">
            Please verify your email address before logging in. If you do not see the
            email, check your spam folder.
          </div>
          <p className="auth-link">
            <Link to={loginPath}>Back to login</Link>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-container">
      <div className="auth-box">
        <h1>Sign Up</h1>
        <p className="auth-subtitle">Create an account to get started</p>

        <form onSubmit={handleSubmit} className="auth-form">
          <div className="form-group">
            <label htmlFor="name">Name (optional)</label>
            <input
              id="name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Your name"
              autoComplete="name"
            />
          </div>

          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              required
              autoComplete="email"
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">Password</label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              required
              autoComplete="new-password"
              minLength={8}
            />
            <small>Must be at least 8 characters</small>
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword">Confirm Password</label>
            <input
              id="confirmPassword"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="••••••••"
              required
              autoComplete="new-password"
            />
          </div>

          <button type="submit" className="btn btn-primary btn-full" disabled={loading}>
            {loading ? 'Creating account...' : 'Sign Up'}
          </button>
        </form>

        <p className="auth-link">
          Already have an account? <Link to={loginPath}>Login here</Link>
        </p>
      </div>
    </div>
  );
};

export default Signup;
